const ready = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }

  callback();
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const normalizePath = (path) => {
  const clean = path.replace(/\/+$/, '');
  return clean || '/';
};

const markCurrentNavigation = () => {
  const currentPath = normalizePath(window.location.pathname);
  const links = document.querySelectorAll('#header-component a[href], .menu-drawer a[href]');

  links.forEach((link) => {
    let linkPath;

    try {
      linkPath = normalizePath(new URL(link.getAttribute('href'), window.location.origin).pathname);
    } catch (_error) {
      return;
    }

    const hasReadableText = link.textContent.trim().length > 0;
    const isIconOnly = !hasReadableText && Boolean(link.querySelector('svg, img'));
    const isCurrent = linkPath === currentPath || (currentPath === '/' && linkPath === '/');

    if (!isIconOnly && isCurrent) {
      link.classList.add('printale-nav-current');
      return;
    }

    link.classList.remove('printale-nav-current');
  });
};

const bindHeaderState = () => {
  const update = () => {
    document.body.classList.toggle('printale-header-is-scrolled', window.scrollY > 24);
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
};

const tiltSelectors = [
  '.product-card',
  '.collection-card',
  '.resource-card',
  '.card',
  '.printale-contact-destination',
  '.printale-faq-card',
  '.printale-faq-category',
].join(',');

const bindTiltCards = () => {
  if (prefersReducedMotion()) return;

  const cards = document.querySelectorAll(tiltSelectors);

  cards.forEach((card) => {
    if (!(card instanceof HTMLElement) || card.dataset.printaleTiltBound === 'true') return;

    card.dataset.printaleTiltBound = 'true';
    card.classList.add('printale-tilt-card');

    card.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;

      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      card.style.setProperty('--printale-tilt-x', `${(-y * 5).toFixed(2)}deg`);
      card.style.setProperty('--printale-tilt-y', `${(x * 6).toFixed(2)}deg`);
    });

    card.addEventListener('pointerleave', () => {
      card.style.removeProperty('--printale-tilt-x');
      card.style.removeProperty('--printale-tilt-y');
      card.style.removeProperty('--printale-tilt-lift');
    });
  });
};

const sparkleTargets = [
  '.button:not(.button-unstyled)',
  '.button-secondary',
  'a.button',
  'button[type="submit"]:not(.button-unstyled)',
  '.shopify-payment-button__button',
  '.printale-contact-destination',
  '.printale-faq-category',
  '[role="radio"]',
  '[aria-pressed]',
].join(',');

const createSparkBurst = (x, y) => {
  if (prefersReducedMotion()) return;

  const burst = document.createElement('span');
  burst.className = 'printale-ui-spark-burst';
  burst.style.setProperty('--printale-spark-x', `${x}px`);
  burst.style.setProperty('--printale-spark-y', `${y}px`);

  const colors = ['#ffd277', '#ff9db6', '#ffffff', '#f7b5d0', '#ffe7a7'];
  const count = 9;

  for (let index = 0; index < count; index += 1) {
    const spark = document.createElement('span');
    const rotation = Math.round((360 / count) * index + Math.random() * 16 - 8);
    const distance = Math.round(22 + Math.random() * 26);

    spark.style.setProperty('--printale-spark-i', String(index));
    spark.style.setProperty('--printale-spark-rot', `${rotation}deg`);
    spark.style.setProperty('--printale-spark-distance', `${distance}px`);
    spark.style.setProperty('--printale-spark-color', colors[index % colors.length]);
    burst.appendChild(spark);
  }

  document.body.appendChild(burst);
  window.setTimeout(() => burst.remove(), 900);
};

const bindSparkles = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest(sparkleTargets) : null;
      if (!target || target.closest('.printale-curtain-intro')) return;

      createSparkBurst(event.clientX, event.clientY);
    },
    { capture: true, passive: true },
  );
};

const observeDynamicCards = () => {
  let debounceTimer = null;

  const observer = new MutationObserver((mutations) => {
    // Ignore spark/canvas mutations from our own effects to avoid feedback loops
    const isOwnEffect = mutations.every((m) =>
      [...m.addedNodes].every(
        (n) =>
          n instanceof Element &&
          (n.classList.contains('printale-ui-spark-burst') ||
            n.classList.contains('printale-click-burst') ||
            n.tagName === 'CANVAS'),
      ),
    );
    if (isOwnEffect) return;
    if (!mutations.some((m) => m.addedNodes.length > 0)) return;

    // Debounce to avoid thrashing on rapid DOM updates
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      bindTiltCards();
      markCurrentNavigation();
    }, 200);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// ─── Konami Code easter egg ───────────────────────────────────────────────────
const bindKonamiCode = () => {
  const sequence = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
  let index = 0;

  document.addEventListener('keydown', (event) => {
    if (event.key === sequence[index]) {
      index += 1;
      if (index === sequence.length) {
        index = 0;
        triggerSpecialScreening();
      }
    } else {
      index = event.key === sequence[0] ? 1 : 0;
    }
  });
};

const triggerSpecialScreening = () => {
  if (prefersReducedMotion()) return;

  // Flash the screen white like a projector light burst
  const flash = document.createElement('div');
  flash.className = 'printale-konami-flash';
  document.body.appendChild(flash);
  window.setTimeout(() => flash.classList.add('is-visible'), 10);
  window.setTimeout(() => flash.remove(), 500);

  // Short delay so the overlay arrives after the flash
  window.setTimeout(() => {
    const overlay = document.createElement('div');
    overlay.className = 'printale-konami-overlay';
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML = `
      <div class="printale-konami-overlay__inner">
        <p class="printale-konami-overlay__kicker">✦ PRIVATE SCREENING UNLOCKED ✦</p>
        <p class="printale-konami-overlay__title">You found the hidden reel.</p>
        <p class="printale-konami-overlay__divider"></p>
        <p class="printale-konami-overlay__sub">Every love story deserves a premiere.</p>
        <p class="printale-konami-overlay__code">Use <strong>PREMIERE10</strong> for 10% off your first order</p>
      </div>
    `;
    document.body.appendChild(overlay);
    window.setTimeout(() => overlay.classList.add('is-visible'), 20);
    window.setTimeout(() => {
      overlay.classList.remove('is-visible');
      window.setTimeout(() => overlay.remove(), 700);
    }, 5000);
  }, 200);
};

// ─── Poster card 5-click "Director's Cut" easter egg ─────────────────────────
// Targets the hero poster on the homepage — no nav interference, no reload risk.
const bindPosterSecret = () => {
  // The poster card is rendered as a custom element; watch for it in case it
  // mounts after this script runs (Shopify section hydration can be async).
  let bound = false;

  const tryBind = () => {
    const poster = document.querySelector('[data-poster-card]');
    if (!poster || bound) return;
    bound = true;

    let clickCount = 0;
    let resetTimer = null;

    poster.addEventListener('click', () => {
      clickCount += 1;
      clearTimeout(resetTimer);
      resetTimer = window.setTimeout(() => { clickCount = 0; }, 4000);

      if (clickCount === 20) {
        clickCount = 0;
        triggerDirectorsCut();
      }
    });
  };

  tryBind();

  // Fallback: poster may not be in DOM yet on first run — observe until found
  if (!document.querySelector('[data-poster-card]')) {
    const mo = new MutationObserver(() => {
      if (document.querySelector('[data-poster-card]')) {
        tryBind();
        mo.disconnect();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
};

const triggerDirectorsCut = () => {
  const overlay = document.createElement('div');
  overlay.className = 'printale-directors-cut-overlay';
  overlay.setAttribute('aria-live', 'assertive');
  overlay.innerHTML = `
    <div class="printale-directors-cut-overlay__inner">
      <p class="printale-directors-cut-overlay__label">🎬 DIRECTOR&#x2019;S CUT</p>
      <p class="printale-directors-cut-overlay__message">You found the secret reel.</p>
      <p class="printale-directors-cut-overlay__code">Use code <strong>DIRECTORSCUT</strong> for 10% off</p>
      <button class="printale-directors-cut-overlay__close" aria-label="Close">✕</button>
    </div>
  `;
  document.body.appendChild(overlay);
  window.setTimeout(() => overlay.classList.add('is-visible'), 20);

  const close = overlay.querySelector('.printale-directors-cut-overlay__close');
  const dismiss = () => {
    overlay.classList.remove('is-visible');
    window.setTimeout(() => overlay.remove(), 500);
  };
  close?.addEventListener('click', dismiss);
  window.setTimeout(dismiss, 8000);
};

ready(() => {
  document.body.classList.add('printale-site-polish');
  bindHeaderState();
  markCurrentNavigation();
  bindTiltCards();
  bindSparkles();
  observeDynamicCards();
  bindKonamiCode();
  bindPosterSecret();
});
